import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectAgentResponseTimeLimitSeconds,
} from '../../redux/features/config/config.selector';
import { selectIsAIAutoMode } from '../../redux/features/ai/ai.selector';
import {
  selectAwaitingResponseSinceMs,
  selectIsCarPresent,
} from '../../selectors/message';
import PulseMeterTile from './PulseMeterTile';

/**
 * RESPONSE TIME (HITL 3.0 Pulse tile).
 *
 * A per-turn SLA meter: it counts up from the customer's last utterance, and the
 * agent must send a TTS reply before the configured limit
 * (`AGENT_RESPONSE_TIME_LIMIT_SECONDS`, default 6s) elapses. The moment the agent
 * replies the tile resets to 0s / empty and waits for the next utterance
 * (see `selectAwaitingResponseSinceMs`).
 *
 * Only shown while the agent has intercepted the AI (the AI is no longer in auto
 * mode — `!isAIAutoMode`, which the agent-interception action sets) AND a car is
 * present. It therefore disappears when the car leaves and once the order is
 * finished/cancelled (interception ends), and is always off unless hitl_3_0 is on.
 *
 * Re-renders once on each whole-second boundary (a self-correcting timeout aimed
 * at the next second stroke) so the numeral flips crisply on the second rather
 * than churning every few ms. The numeral counts whole seconds with no unit
 * suffix; the bars (one per limit second) step in lockstep. Over the limit it
 * stays red.
 */

// Elapsed shown as whole seconds counting up, no unit suffix: 0, 1, 2, 3…
const secondsLabel = (ms: number): string => `${Math.floor(ms / 1000)}`;

const ResponseTimeTile = () => {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const limitSeconds = useAppSelector(selectAgentResponseTimeLimitSeconds);
  const awaitingSinceMs = useAppSelector(selectAwaitingResponseSinceMs);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const isCarPresent = useAppSelector(selectIsCarPresent);

  // "Agent interception has happened" = the AI is no longer driving; the human
  // has stepped in. The agent-interception action flips isAIAutoMode to false.
  const agentIntercepted = !isAIAutoMode;
  const active =
    isHitl3 && agentIntercepted && isCarPresent && limitSeconds > 0;

  const [nowMs, setNowMs] = useState(() => Date.now());

  // Re-render on each whole-second boundary while active AND a customer utterance
  // is awaiting a reply; otherwise the effect stays inert so nothing runs in the
  // background. A self-correcting timeout targets the next second stroke (relative
  // to the utterance) so the numeral flips exactly on the second and never drifts.
  useEffect(() => {
    if (!active || awaitingSinceMs == null) {
      return undefined;
    }
    setNowMs(Date.now()); // recompute immediately on a new utterance

    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNextStroke = () => {
      // ms remaining until the elapsed time next lands on a whole second.
      const msToNextSecond = 1000 - ((Date.now() - awaitingSinceMs) % 1000);
      timeoutId = setTimeout(() => {
        setNowMs(Date.now());
        scheduleNextStroke();
      }, msToNextSecond);
    };
    scheduleNextStroke();
    return () => clearTimeout(timeoutId);
  }, [active, awaitingSinceMs]);

  if (!active) {
    return null;
  }

  const elapsedMs =
    awaitingSinceMs != null ? Math.max(0, nowMs - awaitingSinceMs) : 0;

  return (
    <PulseMeterTile
      label="Response time"
      elapsedMs={elapsedMs}
      limitMs={limitSeconds * 1000}
      formatValue={secondsLabel}
      testId="response-time-tile"
    />
  );
};

export default ResponseTimeTile;
